import { useQuery } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";

import { MASTER_INVALIDATION_KEY } from "@/api/queryClient";
import { useCurrentUserToWorkspaces } from "@/api/resources/usersToWorkspaces/hooks";
import { apiConfigWorkspaces, createWorkspace, deleteWorkspace, updateWorkspace } from "@/api/resources/workspaces/api";
import { workspacesQueries, workspacesQueryKeys } from "@/api/resources/workspaces/queries";
import { selectorDirectAccessWorkspaces, selectorWorkspacesByKey } from "@/api/resources/workspaces/selectors";
import i18n from "@/providers/i18n";
import { spawnCreateMutationHook, spawnDeleteMutationHook, spawnUpdateMutationHook } from "@/utils/api";
import { makeQueryFilterIn, type QueryFilter } from "@/utils/filter";

import type { UserPermissions } from "@/api/resources/auth/types";

const entity = i18n.t("workspace");

export const useWorkspaces = () => {
  return useQuery(workspacesQueries.listAll());
};

export const useWorkspacesByKey = () => {
  return useQuery({ ...workspacesQueries.listAll(), select: selectorWorkspacesByKey });
};

export const useWorkspace = (workspace_id?: string) => {
  return useQuery(workspacesQueries.byId(workspace_id));
};

export const useWorkspaceCustomers = (workspace_id?: string) => {
  return useQuery(workspacesQueries.listCustomerWorkspaces(workspace_id));
};

export const useCurrentWorkspace = () => {
  const { workspace_id } = useParams({ strict: false });
  return useQuery(workspacesQueries.byId(workspace_id));
};

export const useCurrentCustomerWorkspace = () => {
  const { customer_workspace_id } = useParams({ strict: false });
  return useQuery(workspacesQueries.byId(customer_workspace_id));
};

export const useDirectAccessWorkspaces = (userPermissions?: UserPermissions | null) => {
  const { data: usersToWorkspaces } = useCurrentUserToWorkspaces();
  return useQuery({
    ...workspacesQueries.listAll(),
    select: (data) => selectorDirectAccessWorkspaces(data, userPermissions, usersToWorkspaces),
  });
};

export const useCurrentCustomerWorkspaces = () => {
  const { workspace_id } = useParams({ strict: false });
  return useQuery(workspacesQueries.listCustomerWorkspaces(workspace_id));
};

export const useWorkspacesQueryFilters = () => {
  const { data: currentWorkspace } = useCurrentWorkspace();
  const { data: currentCustomerWorkspace } = useCurrentCustomerWorkspace();
  const { data: currentCustomerWorkspaces } = useCurrentCustomerWorkspaces();

  return {
    currentWorkspace: currentWorkspace ? (["workspace_id", "eq", currentWorkspace.id] as QueryFilter) : undefined,
    currentCustomerWorkspace: currentCustomerWorkspace
      ? (["workspace_id", "eq", currentCustomerWorkspace.id] as QueryFilter)
      : undefined,
    currentWorkspaceCustomers: currentCustomerWorkspaces
      ? makeQueryFilterIn({
          items: currentCustomerWorkspaces.map((customer) => customer.id),
          filterKey: "workspace_id",
        })
      : undefined,
    materialFilters: currentWorkspace?.hide_public_materials
      ? [
          makeQueryFilterIn({
            items: [currentWorkspace.id, currentWorkspace.creator_workspace_id],
            filterKey: "workspace_id",
          }),
        ]
      : [],
  };
};

export const useCreateWorkspace = spawnCreateMutationHook({
  mutationFn: createWorkspace,
  notifyEntity: entity,
  analyticsEntity: apiConfigWorkspaces.table,
  invalidationQueryKeys: [MASTER_INVALIDATION_KEY],
});
export const useUpdateWorkspace = (id?: string) =>
  spawnUpdateMutationHook({
    mutationFn: updateWorkspace,
    notifyEntity: entity,
    analyticsEntity: apiConfigWorkspaces.table,
    entityQueryKey: workspacesQueryKeys.byId(id),
    invalidationQueryKeys: [workspacesQueryKeys.lists()],
  })();

export const useDeleteWorkspace = spawnDeleteMutationHook({
  mutationFn: deleteWorkspace,
  notifyEntity: entity,
  analyticsEntity: apiConfigWorkspaces.table,
  invalidationQueryKeys: [MASTER_INVALIDATION_KEY],
});
